// #region imports

// --- Angular
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS
} from '@angular/common/http';

// -- 3rd Party
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';

// --- HS Code
import * as c from './';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { AccountModule } from './account/account.module';
import { AuthGuard } from './core/guards/auth.guard';
import { TokenInterceptorService } from './core/interceptors/token-interceptor.service';
import {
  ActivationBaseService,
  ActivationMockService,
  ActivationService,
  DashboardBaseService,
  DemographicsBaseService,
  LanguagesBaseService,
  LanguagesMockService,
  LanguagesService,
  ProfileBaseService,
  ProfileMockService,
  RegistrationBaseService,
  RegistrationService,
  SurveyBaseService,
  SurveyMockService,
  SurveyService,
  UserDemographicsBaseService,
  ResponseBaseService,
  QuestionsBaseService,
  TeamEnquiryBaseService,
  TeamEnquiryMockService,
  TeamEnquiryService
} from './core/services';
import { StandardHeadersService } from './core/interceptors/standard-headers.service';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { DemographicsService } from './core/services/system/demographics.service';
import { ProfileService } from './core/services/profile/profile.service';
import { UserDemographicsService } from './core/services/profile/user-demographics.service';
import { UserDemographicsMockService } from './core/services/profile/user-demographics-mock.service';
import { AddCsrfInterceptorService } from './core/interceptors/add-csrf-interceptor.service';
import { SurveyBreakBaseService } from './core/services/survey/survey-break-base.service';
import { SurveyBreakMockService } from './core/services/survey/survey-break-mock.service';
import { ResponseService } from './core/services/survey/response.service';
import { ResponseMockService } from './core/services/survey/response-mock.service';
import { QuestionsMockService } from './core/services/questions/questions-mock.service';
import { QuestionsService } from './core/services/questions/questions.service';
import { ResultsService } from './core/services/results/results.service';
import { ResultsBaseService } from './core/services/results/results-base.service';
import { ReportsBaseService } from './core/services/reporting/reports-base.service';
import { ReportsService } from './core/services/reporting/reports.service';
import { UserNotificationBaseService } from './core/services/notifications/user-notification-base.service';
import { UserNotificationService } from './core/services/notifications/user-notification.service';
import { CommunicationsBaseService } from './core/services/profile/communications-base.service';
import { CommunicationsService } from './core/services/profile/communications.service';
import { UserNotificationMockService } from './core/services/notifications/user-notification-mock.service';
import { NetWorkInterceptorService } from './core/interceptors/network-interceptor.service';
import { AnalyticsServiceService } from './core/services/analytics-services/analytics-service.service';

// #endregion

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [c.AppComponent, c.ErrorComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    DeviceDetectorModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    CoreModule,
    SharedModule,
    AccountModule,
  ],
  providers: [
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StandardHeadersService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddCsrfInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetWorkInterceptorService,
      multi: true
    },
    {
      provide: ActivationBaseService,
      useClass: ActivationService
    },
    {
      provide: CommunicationsBaseService,
      useClass: CommunicationsService
    },
    {
      provide: DashboardBaseService,
      useClass: DashboardBaseService
    },
    {
      provide: DemographicsBaseService,
      useClass: DemographicsService
    },
    {
      provide: LanguagesBaseService,
      useClass: LanguagesService
    },
    {
      provide: ProfileBaseService,
      useClass: ProfileService
    },
    {
      provide: QuestionsBaseService,
      useClass: QuestionsService
    },
    {
      provide: RegistrationBaseService,
      useClass: RegistrationService
    },
    {
      provide: ReportsBaseService,
      useClass: ReportsService
    },
    {
      provide: ResponseBaseService,
      useClass: ResponseService
    },
    {
      provide: ResultsBaseService,
      useClass: ResultsService
    },
    {
      provide: SurveyBaseService,
      useClass: SurveyService
    },
    {
      provide: UserDemographicsBaseService,
      useClass: UserDemographicsService
    },
    {
      provide: UserNotificationBaseService,
      useClass: UserNotificationService
    },
    {
      provide: SurveyBreakBaseService,
      useClass: SurveyBreakMockService,
    },
    {
      provide: TeamEnquiryBaseService,
      useClass: TeamEnquiryService,
    }
  ],
  bootstrap: [c.AppComponent]
})
export class AppModule {
  constructor(protected analyticsService: AnalyticsServiceService) { }
}
